import React, { useState, useEffect, useRef } from 'react';
import { UserData } from '../types';
import { playStoreUrl } from '../lib/appLinks';

interface ChatScreenProps {
  userData: UserData | null;
  onBack: () => void;
}

const ChatScreen: React.FC<ChatScreenProps> = ({ userData, onBack }) => {
  const [messages, setMessages] = useState<{role: 'user' | 'ai', content: string}[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (userData && messages.length === 0) {
      interpretDream();
    }
  }, [userData]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const interpretDream = async () => {
    setIsLoading(true);
    setMessages([{ role: 'user', content: userData?.dreamDescription || userData?.dreamContent || '' }]);
    
    try {
      const response = await fetch('/api/interpret', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });
      
      const data = await response.json();
      setMessages(prev => [...prev, { role: 'ai', content: data.interpretation }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'ai', content: 'نعتذر، حدث خطأ أثناء التفسير. يرجى المحاولة مرة أخرى.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1E293B] flex flex-col font-sans" dir="rtl">
      <header className="p-4 border-b border-slate-700 bg-slate-800/50 backdrop-blur-md sticky top-0 z-20 flex items-center justify-between">
        <button onClick={onBack} className="text-emerald-400 p-2">← عودة</button>
        <h2 className="text-white font-bold">تفسير الرؤية</h2>
        <div className="w-8"></div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 space-y-6" ref={scrollRef}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}>
            <div className={`max-w-[85%] p-4 rounded-2xl shadow-lg ${
              msg.role === 'user' 
                ? 'bg-slate-700 text-slate-200 rounded-tr-none' 
                : 'bg-emerald-600 text-white rounded-tl-none'
            }`}>
              <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-end">
            <div className="bg-emerald-600/20 border border-emerald-500/30 p-6 rounded-2xl rounded-tl-none text-emerald-400 flex items-center gap-3">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
              <span>جاري تحليل الرموز...</span>
            </div>
          </div>
        )}

        {!isLoading && messages.some(m => m.role === 'ai') && (
          <div className="bg-slate-800/90 border border-emerald-500/40 rounded-2xl p-5 text-center">
            <h3 className="font-bold text-white mb-1">هل تريد الاحتفاظ بتفسيرك والعودة إليه لاحقاً؟</h3>
            <p className="text-slate-300 text-xs mb-4 leading-relaxed">
              استخدم تطبيق ابن سيرين AI لمزيد من الميزات: سجل التفسيرات، المفضلة، الإدخال الصوتي، واختيار المفسر والمنهج.
            </p>
            <a
              href={playStoreUrl('ai_result')}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold rounded-xl no-underline text-sm transition-all active:scale-95"
            >
              📱 تحميل تطبيق ابن سيرين AI
            </a>
          </div>
        )}
      </main>

      <footer className="p-6 bg-slate-800/80 border-t border-slate-700">
         <p className="text-slate-300 text-xs text-center leading-relaxed">
            هذا التفسير مقدم بناءً على الرموز الواردة في حلمك وفقاً لمنهج ابن سيرين. 
            يرجى العلم أن الرؤى قد تختلف باختلاف أحوال الرائي.
         </p>
      </footer>
    </div>
  );
};

export default ChatScreen;
